import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Divider from '@amzn/meridian/divider';
import { adminApi, DashboardPayload } from '../service/adminApiService';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { MetricTile } from '../components/MetricTile';
import { Section } from '../components/Section';

const ACTION_LABELS: Record<string, string> = {
  create_group: 'Created group',
  update_group: 'Updated group',
  delete_group: 'Deleted group',
  upsert_user: 'Updated user',
  delete_user: 'Deleted user',
  bulk_import_users: 'Bulk imported users',
  bulk_delete_users: 'Bulk deleted users',
  create_template: 'Created template',
  update_template: 'Updated template',
  delete_template: 'Deleted template',
  create_department: 'Created department',
  update_department: 'Updated department',
  delete_department: 'Deleted department',
  rbac_exported: 'Exported RBAC data',
};

function formatTimestamp(ts: string): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function timeAgo(ts: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatTimestamp(ts);
}

export const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const refresh = useCallback(
    async (force = false) => {
      setLoading(true);
      setError(null);
      try {
        const payload = await adminApi.getDashboard(force);
        setData(payload);
        setLastRefresh(new Date());
      } catch (err: any) {
        const message = err?.message || 'Failed to load dashboard';
        setError(message);
        toast.push({ type: 'error', message });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const subtitle = lastRefresh
    ? `Last updated ${timeAgo(lastRefresh.toISOString())}`
    : 'Overview of the RBAC configuration at a glance';

  return (
    <Column spacing="large">
      <PageHeader
        title="Dashboard"
        subtitle={subtitle}
        actions={
          <Button type="tertiary" size="small" onClick={() => refresh(true)} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      {error && <Alert type="error">{error}</Alert>}

      {loading && !data ? (
        <Row alignmentHorizontal="center" spacingInset="800">
          <Loader type="circular" size="large" />
        </Row>
      ) : data ? (
        <Column spacing="large">
          {/* Metric tiles */}
          <Row wrap="down" spacing="medium">
            <MetricTile label="Total groups" value={data.total_groups} hint="POSIX policy records" />
            <MetricTile label="Total users" value={data.total_users} hint="User-group mappings" />
            <MetricTile
              label="Accounts with prod access"
              value={data.accounts_with_prod_access}
              hint="Union across prod-enabled groups"
              accent="warning"
            />
            <MetricTile
              label="Admin users"
              value={data.admin_user_count}
              hint={`Members of ft-cogit-admin`}
              accent="primary"
            />
          </Row>

          {/* Quick actions */}
          <Section
            title="Quick actions"
            subtitle="Common admin tasks — audited and reversible except where noted."
          >
            <Row spacing="small" wrap="down">
              <Button type="primary" onClick={() => navigate('/admin/groups/new')}>
                + New group
              </Button>
              <Button type="secondary" onClick={() => navigate('/admin/users/new')}>
                + Add user
              </Button>
              <Button type="tertiary" onClick={() => navigate('/admin/users/bulk-import')}>
                Bulk import users
              </Button>
              <Button type="tertiary" onClick={() => navigate('/admin/audit')}>
                View audit log
              </Button>
            </Row>
          </Section>

          {/* Two-column lists */}
          <Row widths={['fill', 'fill']} spacing="medium" alignmentVertical="top" wrap="down">
            <Section
              title="Recently updated groups"
              actions={
                <Button type="link" size="small" onClick={() => navigate('/admin/groups')}>
                  View all →
                </Button>
              }
            >
              {data.recently_updated_groups.length === 0 ? (
                <Text color="secondary">No groups updated yet.</Text>
              ) : (
                <Column spacing="none">
                  {data.recently_updated_groups.map((g, idx) => (
                    <React.Fragment key={g.posix_group}>
                      {idx > 0 && <Divider />}
                      <div style={{ padding: '12px 0' }}>
                        <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down">
                          <Column spacing="none" minWidth={0}>
                            <Link
                              to={`/admin/groups/${encodeURIComponent(g.posix_group)}`}
                              style={{ color: '#0972D3', fontWeight: 600, textDecoration: 'none' }}
                            >
                              {g.posix_group}
                            </Link>
                            <Text type="b200" color="secondary">
                              {g.department || '(no department)'}
                            </Text>
                          </Column>
                          <Text type="b200" color="secondary">
                            {timeAgo(g.last_updated)}
                          </Text>
                        </Row>
                      </div>
                    </React.Fragment>
                  ))}
                </Column>
              )}
            </Section>

            <Section
              title="Recent audit events"
              actions={
                <Button type="link" size="small" onClick={() => navigate('/admin/audit')}>
                  View all →
                </Button>
              }
            >
              {data.recent_audit_events.length === 0 ? (
                <Column spacing="small" alignmentHorizontal="start" spacingInset="400">
                  <Text type="b300">No admin activity yet.</Text>
                  <Text type="b200" color="secondary">
                    Create a group or add a user to see events here. Every admin action is
                    logged for two years.
                  </Text>
                </Column>
              ) : (
                <Column spacing="none">
                  {data.recent_audit_events.map((e, idx) => (
                    <React.Fragment key={e.change_id}>
                      {idx > 0 && <Divider />}
                      <div style={{ padding: '12px 0' }}>
                        <Row alignmentHorizontal="justify" alignmentVertical="top" wrap="down">
                          <Column spacing="none" minWidth={0}>
                            <Text type="b300">
                              {ACTION_LABELS[e.action] || e.action}
                              {': '}
                              <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                                {e.target_id}
                              </span>
                            </Text>
                            <Text type="b200" color="secondary">
                              {e.actor_alias || 'system'}
                            </Text>
                          </Column>
                          <Text type="b200" color="secondary">
                            {timeAgo(e.timestamp)}
                          </Text>
                        </Row>
                      </div>
                    </React.Fragment>
                  ))}
                </Column>
              )}
            </Section>
          </Row>
        </Column>
      ) : null}
    </Column>
  );
};
